<script setup lang="ts">
import stores from '@jaaluu/stores';
import { FavoriteGroup } from '@jaaluu/stores/favorites';
import NumberFlow from '@number-flow/vue';
import { t } from '@shared/i18n';
import sharedStores from '@shared/shared-stores';
import { computed, onMounted, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import Dialog from './Dialog.vue';
import TextWithTooltips from './TextWithTooltips.vue';

const { initSynapsisScore } = stores.tourGuide.funcs;

const { isFullscreenPage } = stores.app.funcs;
const { animate } = sharedStores.animate.funcs;
const { actionSounds } = stores.settings.state;
const { setActionSoundsFromLocalStorage } = stores.settings.funcs;
const { favoriteGroups, openSynapsisScoreDialog } = stores.favorites.state;

const route = useRoute();
const baseUrl = (import.meta as any).env.VITE_JAALUU_API_URL;
const bellAudio = new Audio(`${baseUrl}/v1/file/sounds/bell.mp3`);
bellAudio.volume = 0.6;

const { langForeign, langNative } = stores.language.state;
const { progress } = stores.progress.state;
const { courseProgress } = stores.courseProgress.state;
const { grammarProgress } = stores.grammarProgress.state;

const scoreDialogIsOpen = ref(false);
const totalWords = ref(0);
const totalScoreButton = ref(0);
const totalScore = ref(0);
const funFacts = ref<string[]>([]);
const scorePerLevel = computed(() => {
  const levels: { [level: number]: number } = {};
  for (let level = 0; level <= 6; level++) {
    levels[level] = 0;
  }
  return levels;
});

onMounted(() => {
  setActionSoundsFromLocalStorage();
  animateNumbers();
  animateButtonNumber();
});

const animateButtonNumber = () => {
  animate('#main-score-button', 'scale-sm');
  if (actionSounds.value)
    bellAudio.play().catch((err) => console.error('Error playing bell sound:', err));
  setTimeout(() => {
    animate('#brain-icon', 'scale-sm');
  }, 900);
  setTimeout(() => {
    totalScoreButton.value = getTotalScore();
  }, 1500);
};

const animateNumbers = () => {
  totalScore.value = 0;
  totalWords.value = 0;
  for (let level = 0; level <= 6; level++) {
    scorePerLevel.value[level] = 0;
  }
  setTimeout(() => {
    setTotalWords();
    setTotalScore();
    setScoresPerLevel();
  }, 300);
};

const getGroupWordCount = (group: FavoriteGroup): number => {
  return group.words.length;
};

const getGroupScore = (group: FavoriteGroup): number => {
  return group.words.reduce((sum, word) => sum + word.srs_level + 1, 0);
};

const getFinishedSubcategoriesCount = (): number => {
  if (!langForeign.value || !langNative.value || !progress.value) return 0;
  const langProgress = (progress.value as any)[langForeign.value]?.[langNative.value];
  if (!langProgress) return 0;
  let count = 0;
  for (const catType of Object.values(langProgress) as any[]) {
    for (const mainCat of Object.values(catType) as any[]) {
      for (const subCat of Object.values(mainCat) as any[]) {
        if (subCat.pct === 100) count++;
      }
    }
  }
  return count;
};

const getFinishedSubcategoriesByType = (): Record<string, number> => {
  if (!langForeign.value || !langNative.value || !progress.value) return {};
  const langProgress = (progress.value as any)[langForeign.value]?.[langNative.value];
  if (!langProgress) return {};
  const result: Record<string, number> = {};
  for (const catType of Object.keys(langProgress)) {
    let count = 0;
    for (const mainCat of Object.values(langProgress[catType]) as any[]) {
      for (const subCat of Object.values(mainCat) as any[]) {
        if ((subCat as any).pct === 100) count++;
      }
    }
    if (count > 0) result[catType] = count;
  }
  return result;
};

const getCategoryBonus = (): number => getFinishedSubcategoriesCount() * 50;

const getFinishedCourseChaptersCount = (): number => {
  return courseProgress.value.stories.filter((s) => s.completed).length;
};

const getCourseChapterBonus = (): number => getFinishedCourseChaptersCount() * 50;

const getCompletedGrammarChaptersCount = (): number => {
  return grammarProgress.value.completedChapters;
};

const getGrammarChapterBonus = (): number => getCompletedGrammarChaptersCount() * 50;

const getTotalScore = (): number => {
  const wordsScore = Object.values(favoriteGroups.value).reduce((total, group) => {
    return total + getGroupScore(group);
  }, 0);
  return wordsScore + getCategoryBonus() + getCourseChapterBonus() + getGrammarChapterBonus();
};

const setTotalScore = () => {
  totalScore.value = getTotalScore();
};

const setTotalWords = () => {
  totalWords.value = Object.values(favoriteGroups.value).reduce((total, group) => {
    return total + getGroupWordCount(group);
  }, 0);
};
const setScoresPerLevel = () => {
  for (let level = 0; level <= 6; level++) {
    scorePerLevel.value[level] = getScoreForLevel(level);
  }
};

const getWordsForLevel = (level: number): number => {
  return Object.values(favoriteGroups.value).reduce((total, group) => {
    return total + group.words.filter((word) => word.srs_level === level).length;
  }, 0);
};

const getScoreForLevel = (level: number): number => {
  return Object.values(favoriteGroups.value).reduce((total, group) => {
    return (
      total +
      group.words
        .filter((word) => word.srs_level === level)
        .reduce((sum, word) => sum + word.srs_level + 1, 0)
    );
  }, 0);
};

const _totalScore = computed(() => {
  return getTotalScore();
});

const visibleFunFactsCount = computed(() => Math.floor(getTotalScore() / 100));

const pointsToNextFunFact = computed(() => {
  const score = getTotalScore();
  return (100 - (score % 100)) % 100;
});

const fetchFunFacts = async () => {
  if (!langForeign.value) return;
  try {
    const res = await fetch(`${baseUrl}/v1/fun-facts/${langForeign.value}`);
    if (!res.ok) return;
    const data = await res.json();
    funFacts.value = data.fun_facts ?? [];
  } catch {
    // ignore
  }
};

watch(
  () => scoreDialogIsOpen.value,
  (isOpen) => {
    animateNumbers();
    if (isOpen) fetchFunFacts();
  }
);
watch(
  () => _totalScore.value,
  () => {
    animateButtonNumber();
  }
);
watch(openSynapsisScoreDialog, (open) => {
  if (open) {
    scoreDialogIsOpen.value = true;
    initSynapsisScore();
    openSynapsisScoreDialog.value = false;
  }
});
</script>

<template>
  <!-- Fixed Bottom Left Button -->
  <v-btn
    v-if="!isFullscreenPage(route)"
    @click="
      () => {
        scoreDialogIsOpen = true;
        initSynapsisScore();
      }
    "
    fab
    large
    id="main-score-button"
    color="blue"
    class="synapsis-score-button"
    elevation="8"
  >
    <div class="d-flex align-center">
      <div id="brain-icon-overlay"></div>
      <v-icon id="brain-icon" size="24" icon="mdi-brain" class="mb-1" />
      <span class="text-caption font-weight-bold">
        <NumberFlow :value="totalScoreButton" />
      </span>
    </div>
  </v-btn>

  <Dialog
    icon="mdi-brain"
    v-model="scoreDialogIsOpen"
    max-width="500"
    :title="t('general.synapsis_score')"
    @close="() => (scoreDialogIsOpen = false)"
  >
    <!-- Total Score -->
    <div class="">
      <div class="d-flex justify-space-between align-center title">
        <div class="">{{ t('general.total') }}:</div>
        <div><NumberFlow :value="totalScore" /></div>
      </div>
    </div>
    <v-divider class="my-4"></v-divider>

    <div>
      <div class="title-sm mb-2">{{ t('general.words', 2) }}</div>
      <div v-for="level in [0, 1, 2, 3, 4, 5, 6]">
        <div
          class="d-flex justify-space-between align-center mb-2"
          v-if="getScoreForLevel(level) > 0"
        >
          <v-icon icon="mdi-star" class="mr-2" color="green" />
          <div class="w-level">{{ t('general.level') }} {{ level + 1 }}:</div>
          <div class="words d-flex align-center">
            <NumberFlow :value="getWordsForLevel(level)" />
            <div class="ml-1">
              {{ t('general.words', getWordsForLevel(level)) }}
            </div>
          </div>
          <div>x</div>
          <div>{{ level + 1 }} {{ t('general.points', level + 1) }}</div>
          <div class="score d-flex align-center"><div class="mr-5">=</div><NumberFlow :value="scorePerLevel[level]" /></div>
        </div>
      </div>
    </div>
    <template v-if="getCategoryBonus() > 0">
      <v-divider class="my-4"></v-divider>
      <div class="title-sm mb-2">{{ t('general.finished_units', 2) }}</div>
      <div
        v-for="[catType, count] in Object.entries(getFinishedSubcategoriesByType())"
        :key="catType"
        class="d-flex justify-space-between align-center mb-2"
      >
        <div class="words d-flex align-center">
          <v-icon icon="mdi-trophy" class="mr-2" color="green" />
          <NumberFlow :value="(count as number)" />
          <div class="mx-1">
            x {{ t('category.' + catType) }}
          </div>
          <div class="ml-1">x 50 {{ t('general.points', 2) }}</div>
        </div>
        <div class="score d-flex align-center"><div class="mr-5">=</div><NumberFlow :value="(count as number) * 50" /></div>
      </div>
    </template>
    <template v-if="getCourseChapterBonus() > 0">
      <v-divider class="my-4"></v-divider>
      <div class="title-sm mb-2">{{ t('general.finished_course_chapters', 2) }}</div>
      <div class="d-flex justify-space-between align-center mb-2">
        <div class="words d-flex align-center">
          <v-icon icon="mdi-medal" class="mr-2" color="green" />
          <NumberFlow :value="getFinishedCourseChaptersCount()" />
          <div class="mx-1">
            {{ t('general.finished_course_chapters', getFinishedCourseChaptersCount()) }}
          </div>
          <div class="ml-1">x 50 {{ t('general.points', 2) }}</div>
        </div>
        <div class="score d-flex align-center"><div class="mr-5">=</div><NumberFlow :value="getCourseChapterBonus()" /></div>
      </div>
    </template>
    <template v-if="getGrammarChapterBonus() > 0">
      <v-divider class="my-4"></v-divider>
      <div class="title-sm mb-2">{{ t('general.grammar_chapters', 2) }}</div>
      <div class="d-flex justify-space-between align-center mb-2">
        <div class="words d-flex align-center">
          <v-icon icon="mdi-book-open" class="mr-2" color="green" />
          <NumberFlow :value="getCompletedGrammarChaptersCount()" />
          <div class="mx-1">
            {{ t('general.grammar_chapters', getCompletedGrammarChaptersCount()) }}
          </div>
          <div class="ml-1">x 50 {{ t('general.points', 2) }}</div>
        </div>
        <div class="score d-flex align-center"><div class="mr-5">=</div><NumberFlow :value="getGrammarChapterBonus()" /></div>
      </div>
    </template>
    <template v-if="visibleFunFactsCount > 0 || pointsToNextFunFact > 0">
      <v-divider class="my-4"></v-divider>
      <template v-if="visibleFunFactsCount > 0">
        <div class="text-caption font-weight-bold mb-2">
          {{ t('general.fun_facts', { country: t('countries.' + langForeign) }) }}
        </div>
        <v-expansion-panels v-if="visibleFunFactsCount > 1" class="mb-2" elevation="0">
          <v-expansion-panel>
            <v-expansion-panel-title class="text-caption">
              {{ t('general.fun_facts_previous', { count: visibleFunFactsCount - 1 }) }}
            </v-expansion-panel-title>
            <v-expansion-panel-text>
              <div
                v-for="(fact, i) in funFacts.slice(0, visibleFunFactsCount - 1)"
                :key="i"
                class="fun-fact mb-2"
              >
                😄 <TextWithTooltips :text="fact" />
              </div>
            </v-expansion-panel-text>
          </v-expansion-panel>
        </v-expansion-panels>
        <div v-if="funFacts[visibleFunFactsCount - 1]" class="fun-fact mb-2">
          😄 <TextWithTooltips :text="funFacts[visibleFunFactsCount - 1]" />
        </div>
      </template>
      <v-alert
        v-if="pointsToNextFunFact >= 0"
        size="small"
        variant="tonal"
        color="info"
        class="mb-1"
      >
        <div>
          <div>
            <v-icon start size="14" icon="mdi-lightbulb-outline" />
            {{ t('general.fun_facts_missing_points', { country: t('countries.' + langForeign) }) }}:
            <NumberFlow
              :value="pointsToNextFunFact === 0 ? 100 : pointsToNextFunFact"
              class="ml-1 bold"
            />
          </div>
        </div>
      </v-alert>
    </template>
  </Dialog>
</template>

<style scoped>
.v-list-item {
  border-bottom: 1px solid rgba(0, 0, 0, 0.12);
}

.v-list-item:last-child {
  border-bottom: none;
}

.synapsis-score-button {
  position: fixed !important;
  bottom: -1px;
  left: -1px;
  z-index: 1000;
}
@media screen and (min-width: 600px) {
  .synapsis-score-button {
    bottom: 20px;
    left: 20px;
  }
}

.words {
  min-width: 80px;
  text-align: right;
}
.score {
  width: 55px;
}

.fun-fact-hint {
  opacity: 0.7;
}

.fun-fact {
  font-size: 0.85rem;
  opacity: 0.85;
  line-height: 1.4;
}

.w-level {
  min-width: 80px;
}

/* #brain-icon {
  opacity: 0.8;
}
#brain-icon-overlay {
  position: relative;
  top: -7px;
  left: 5px;

  &::before {
    content: '';
    position: absolute;
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background: radial-gradient(circle, rgb(255, 255, 255) 80%, transparent 99%);
    animation: spotlight-move 2s ease-in-out infinite;
  }
}

@keyframes spotlight-move {
  0%,
  100% {
    transform: translate(-20%, -20%);
  }
  50% {
    transform: translate(60%, -20%);
  }
} */
</style>
